import * as React from 'react';
import classnames from '../utils/classnames.tsx';
import { useDropzone } from 'react-dropzone';
import { useControllableState } from '../utils/useControllableState.tsx';
import Button from './Button.tsx';
import { withIcon } from './Icon.tsx';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';

interface IProps {
    className?: string;
    placeholder: string;
    placeholderActive?: string;
    placeholderInvalid?: string;
    maxFiles?: number;
    onFilesChange?: (files: any[]) => void;
    onDropRejected?: (fileRejections: any[]) => void;
    accept?: { [key: string]: string[] };
    files?: any[];
    renderFileDetails?: (file: any) => React.ReactNode;
}

interface IPropsFile {
    file: any;
    onRemove?: (file: any) => void;
    details?: React.ReactNode;
}

const RemoveIcon = withIcon(faTrash);

const DropAreaFile = ({ file, onRemove, details }: IPropsFile) => {
    return (
        <div className="w-full flex justify-between items-center border border-solid border-line-weak rounded p-3 text-content-strong">
            <div className="flex flex-col gap-0.5">
                {file.url ? (
                    <a
                        href={file.url}
                        className="text-sm font-medium px-1 text-content-brand-default hover:text-content-brand-hover"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {file.name}
                    </a>
                ) : (
                    <span className="text-sm font-medium px-1">{file.name}</span>
                )}
                {details && <span className="text-xs text-content-weak px-1">{details}</span>}
            </div>
            <Button variant="ghost" onClick={onRemove} className="text-content-danger-strong" type="button">
                <RemoveIcon />
            </Button>
        </div>
    );
};

const DropArea = ({
    className,
    placeholder,
    placeholderActive,
    placeholderInvalid,
    maxFiles = 0,
    files: propsFiles,
    onFilesChange,
    onDropRejected,
    renderFileDetails,
    accept = {
        'application/pdf': ['.pdf'],
        'image/png': ['.png'],
        'image/jpeg': ['.jpeg', '.jpg'],
        'image/tiff': ['.tiff', '.tif'],
        'application/vnd.ms-excel': ['.xls', '.csv'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
        'text/csv': ['.csv'],
        'message/rfc822': ['.eml', '.msg'],
        'text/html': ['.html', '.htm'],
        'text/xml': ['.x83', '.xml'],
        'application/xml': ['.xml'],
        'application/x-hwp': ['.hwp'],
        'application/zip': ['.zip'],
        'application/rtf': ['.rtf'],
        'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
        'text/plain': ['.txt'],
    },
}: IProps) => {
    const [files, setFiles] = useControllableState([], propsFiles, onFilesChange);

    const handleDrop = (acceptedFiles) => {
        setFiles((files) => [...files, ...acceptedFiles]);
    };
    const handleRemoveFile = (file) => {
        setFiles(files.filter((f) => f !== file));
    };

    const multiple = maxFiles !== 1;
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
        onDrop: handleDrop,
        onDropRejected,
        multiple,
        accept,
    });

    let displayPlaceholder = placeholder;
    if (isDragReject && placeholderInvalid) displayPlaceholder = placeholderInvalid;
    else if (isDragActive && placeholderActive) displayPlaceholder = placeholderActive;

    return (
        <div className={classnames('flex flex-col gap-2', className)}>
            {(!files.length || multiple) && (
                <div
                    className={classnames(
                        'w-full h-40 rounded flex items-center justify-center bg-surface-medium hover:bg-surface-strong transition-colors p-4'
                    )}
                    {...getRootProps()}
                >
                    <input {...getInputProps()} />
                    <span className="text-sm text-content-weak">{displayPlaceholder}</span>
                </div>
            )}

            {!!files.length && (
                <div className="flex flex-col gap-2">
                    {files.map((file, i) => (
                        <DropAreaFile
                            key={i}
                            file={file}
                            onRemove={() => handleRemoveFile(file)}
                            details={renderFileDetails?.(file)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default DropArea;
